import { Component, OnInit, computed, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { OrganizationStoreService } from '../../../../shared/services/organization-store.service';
import { ApiOrganizationService, ApiSurvey } from '../../../../shared/services/api-organization.service';
import { UserStoreService } from '../../../../shared/services/user-store.service';
import { ApiErrorResponse } from '../../../../shared/models/api-error-response.interface';
import { ApiSurveyCreateErrorCode, ApiSurveyCreateService } from '../../../../shared/services/api-survey-create.service';
import { RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { BreadcrumbComponent } from '../../../../core/layout/components/breadcrumb/breadcrumb.component';

@Component({
  selector: 'app-surveys-management',
  templateUrl: './surveys-management.component.html',
  styleUrls: ['./surveys-management.component.css'],
  imports: [RouterLink, FormsModule, BreadcrumbComponent]
})
export class SurveysManagementComponent implements OnInit {
  copiedText = signal<string | null>(null);

  readonly canEdit = computed(() => this.userStore.isAdmin());

  // create / clone — both collect the same ID, name and type, so they share one dialog
  readonly surveyTypes = ['SERVICE', 'SALES', 'SHOPPER', 'PRODUCT', 'DELIVERY', 'USEDCAR', 'CONSIDERATION', 'RELATIONAL', 'CLOSEDLOOP', 'COMMS'];
  private readonly surveyIdPattern = /^[A-Za-z0-9-]+$/;

  dialogMode = signal<'create' | 'clone' | null>(null);
  cloneSource = signal<ApiSurvey | null>(null);
  newSurveyId = '';
  newSurveyName = '';
  newSurveyType = '';
  saving = signal(false);
  dialogError = signal<string | null>(null);

  constructor(
    public organizationStore: OrganizationStoreService,
    private userStore: UserStoreService,
    private apiOrganizationService: ApiOrganizationService,
    private surveyCreateService: ApiSurveyCreateService
  ) {}

  ngOnInit(){}

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.copiedText.set(text);
      setTimeout(() => this.copiedText.set(null), 1500);
    });
  }

  // ── Create / Clone ───────────────────────────────────────────────────────

  requestCreate(): void {
    if (!this.canEdit() || !this.organizationStore.selectedOrganizationId()) return;
    this.openDialog('create', null);
  }

  requestClone(survey: ApiSurvey): void {
    if (!this.canEdit()) return;
    this.openDialog('clone', survey);
  }

  private openDialog(mode: 'create' | 'clone', source: ApiSurvey | null): void {
    this.dialogMode.set(mode);
    this.cloneSource.set(source);
    this.newSurveyId = '';
    this.newSurveyName = '';
    this.newSurveyType = '';
    this.dialogError.set(null);
  }

  cancelDialog(): void {
    if (this.saving()) return;
    this.dialogMode.set(null);
    this.cloneSource.set(null);
  }

  get isSurveyIdValid(): boolean {
    return this.surveyIdPattern.test(this.newSurveyId.trim());
  }

  get isSurveyIdDuplicate(): boolean {
    const id = this.newSurveyId.trim().toLowerCase();
    if (!id) return false;
    return this.organizationStore.selectedOrganizationSurveys().some(s => s.id.toLowerCase() === id);
  }

  get surveyIdError(): string | null {
    const id = this.newSurveyId.trim();
    if (!id) return null;
    if (!this.isSurveyIdValid) return 'Only letters, numbers, and - are allowed, with no spaces.';
    if (this.isSurveyIdDuplicate) return 'A survey with this ID already exists in this organization.';
    return null;
  }

  get canSubmitDialog(): boolean {
    return this.isSurveyIdValid && !this.isSurveyIdDuplicate && !!this.newSurveyName.trim() && !!this.newSurveyType.trim();
  }

  confirmDialog(): void {
    if (!this.canEdit() || !this.canSubmitDialog) return;
    const mode = this.dialogMode();
    const source = this.cloneSource();
    const organizationId = this.organizationStore.selectedOrganizationId();
    if (!mode || !organizationId) return;
    if (mode === 'clone' && !source) return;

    this.saving.set(true);
    this.dialogError.set(null);

    const id = this.newSurveyId.trim();
    const name = this.newSurveyName.trim();
    const type = this.newSurveyType.trim();
    const request$: Observable<unknown> = mode === 'clone'
      ? this.surveyCreateService.cloneSurvey(organizationId, source!.id, id, name, type)
      : this.surveyCreateService.createSurvey(organizationId, id, name, type);

    request$.subscribe({
      next: () => {
        this.saving.set(false);
        this.dialogMode.set(null);
        this.cloneSource.set(null);
        this.refreshOrganizations();
      },
      error: (err) => {
        this.saving.set(false);
        this.dialogError.set(this.formatApiError(err, `Failed to ${mode} survey. Please try again.`));
      }
    });
  }

  private refreshOrganizations(): void {
    const currentOrgId = this.organizationStore.selectedOrganizationId();
    this.apiOrganizationService.getOrganizations().subscribe(orgs => {
      this.organizationStore.setOrganizations(orgs, currentOrgId);
    });
  }

  private formatApiError(err: unknown, fallback: string): string {
    if (err instanceof HttpErrorResponse) {
      const apiError = err.error as ApiErrorResponse<ApiSurveyCreateErrorCode> | undefined;
      if (apiError?.code && apiError?.description) {
        const details = apiError.details?.length
          ? ` (${apiError.details.map(d => typeof d === 'string' ? d : JSON.stringify(d)).join('; ')})`
          : '';
        return `[${apiError.code}] ${apiError.description}${details}`;
      }
      return `${fallback} | ${err.message}`;
    }
    return `${fallback} | ${err}`;
  }
}
